// O mapa da lente de localidade de Solicitações (a tela /requested do
// web-chat): a mesma instância Mapbox dos resultados de hotel e carro
// (`shared/results_page/results_map`), com PINOS de hotel e aeroporto que o
// produto desenha e um TRECHO — a linha do voo em foco, escalas incluídas.
// Vive no kit porque o `mapbox-gl` mora aqui; o web-chat só compõe pinos e
// trecho a partir das viagens e reage ao clique no pino.
import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { mapbox, type mapboxgl } from '../shared/results_page/mapbox_module.js';
import {
  liveMap,
  MapViewControls,
  useMapboxContainerResize,
  useMapboxInstance,
  useMapInitializationFailure,
  type MapboxInstance,
  validMapCoordinates,
  type MapCoordinates,
  type MapViewControlsCopy,
} from '../shared/results_page/results_map.js';
import { useKeyedValue } from '../shared/use_keyed_value.js';
import { useStableCallback } from '../shared/use_stable_callback.js';
import './lens_map.css';

/** `point`: um ponto de trajeto (o KM), a pílula só com a letra. */
export type LensMapPinKind = 'hotel' | 'airport' | 'point';

export type LensMapPin = {
  id: string;
  kind: LensMapPinKind;
  /** O que a pílula mostra e o nome acessível do pino: o hotel ou o código
   *  IATA do aeroporto. */
  label: string;
  coordinates: MapCoordinates;
  /** Fora do foco atual (outra viagem, aeroporto fora do trecho): apagado, mas
   *  clicável — é o caminho para trocar o foco. */
  muted?: boolean;
  selected?: boolean;
};

export type LensMapRoute = {
  /** Origem, escalas e destino, na ordem do voo. */
  points: readonly MapCoordinates[];
};

/** Uma linha de trajeto (o KM), desenhada como chega, sem arco: quem chama já
 *  traçou a rua ou a curva. `dashed` é o traçado de quando a rota real não veio. */
type LensMapLine = {
  points: readonly MapCoordinates[];
  dashed?: boolean;
};

type LensMapPadding = { top: number; right: number; bottom: number; left: number };

export type LensMapView = {
  accessToken?: string;
  /** A cidade da lente: onde a câmera nasce e para onde "reenquadrar" volta. */
  center: MapCoordinates;
  fallbackLabel: string;
};

export type LensMapProps = {
  map: LensMapView;
  pins: readonly LensMapPin[];
  /** O voo em foco; sem ele não há linha no mapa. */
  route?: LensMapRoute;
  /** As linhas do trajeto, uma por perna; o trecho do voo é o `route`. */
  lines?: readonly LensMapLine[];
  /** A folga do enquadramento; sem ela, a dos pinos em pílula da lente. */
  padding?: LensMapPadding;
  controls: MapViewControlsCopy;
  onPinClick?: (pinId: string) => void;
};

type MapRef = { readonly current: mapboxgl.Map | null };

/** O zoom de CIDADE — onde a lente nasce e para onde um foco de um ponto só vai:
 *  um ponto não tem caixa para enquadrar. */
const CITY_ZOOM = 12;
/** Teto do `fitBounds`: hotéis na mesma quadra não levam o mapa ao nível da rua. */
const FOCUS_MAX_ZOOM = 14;
const ROUTE_SOURCE = 'iuk-lens-route';
const ROUTE_CASING_LAYER = `${ROUTE_SOURCE}-casing`;
/** Pintura da linha em literal porque o paint do Mapbox não lê variável CSS (os
 *  tokens do DS são `oklch`, que ele não interpreta): a tinta do DS
 *  (`--sand-800`, o neutral-950) e o branco do cartão como recorte. */
const ROUTE_INK = '#0a0a0a';
const ROUTE_CASING = '#ffffff';
const LINES_SOURCE = 'iuk-lens-lines';
const LINES_CASING_LAYER = `${LINES_SOURCE}-casing`;
const LINES_DASHED_LAYER = `${LINES_SOURCE}-dashed`;
/** Os pinos são pílulas ACIMA da coordenada: sem folga em cima a de mais ao
 *  norte sai pela borda. */
const FOCUS_PADDING: LensMapPadding = { top: 72, right: 72, bottom: 48, left: 72 };

function pinPart(className: string): HTMLSpanElement {
  const part = document.createElement('span');
  part.className = className;
  return part;
}

/** O pino: pílula com glifo (cama ou avião) e nome, bico na coordenada. Em
 *  HTML cru porque vai num `Marker` do Mapbox, fora do React; o glifo entra por
 *  máscara no CSS e a palavra dele é o próprio rótulo. */
function pinElement(pin: LensMapPin, onClick: (pinId: string) => void): HTMLButtonElement {
  const element = document.createElement('button');
  element.type = 'button';
  element.className = 'iuk-lens-pin';
  element.dataset.kind = pin.kind;
  element.dataset.muted = String(pin.muted === true);
  element.dataset.selected = String(pin.selected === true);
  element.setAttribute('aria-label', pin.label);
  element.setAttribute('aria-pressed', String(pin.selected === true));
  // Explícito num `<button>` porque o `Marker` do Mapbox carimba `role="img"`
  // em todo elemento que chega sem `role`: o leitor de tela via uma imagem
  // "pressionada", não um botão.
  element.setAttribute('role', 'button');
  const body = pinPart('iuk-lens-pin-body');
  body.append(pinPart('iuk-lens-pin-glyph'));
  const name = pinPart('iuk-lens-pin-name');
  name.textContent = pin.label;
  body.append(name);
  element.append(body, pinPart('iuk-lens-pin-tail'));
  // O ponto de um trajeto marca o lugar exato com um círculo; o bico aponta para ele.
  if (pin.kind === 'point') element.append(pinPart('iuk-lens-pin-dot'));
  element.addEventListener('click', (event) => {
    // O clique é do pino, não do mapa: sem isto o Mapbox trataria como clique
    // no chão e quem escuta o mapa limparia a seleção que o pino acabou de fazer.
    event.stopPropagation();
    onClick(pin.id);
  });
  return element;
}

/** Arco de grande círculo entre dois pontos, interpolado: a reta em lng/lat
 *  fica falsa em trecho longo (GRU → LIS sai por baixo da África), e o arco é a
 *  linha que o olho espera de um voo. */
function greatCircleArc(a: MapCoordinates, b: MapCoordinates, steps = 48): [number, number][] {
  const toRad = (degrees: number) => (degrees * Math.PI) / 180;
  const toDeg = (radians: number) => (radians * 180) / Math.PI;
  const lat1 = toRad(a.latitude);
  const lon1 = toRad(a.longitude);
  const lat2 = toRad(b.latitude);
  const lon2 = toRad(b.longitude);
  const distance =
    2 *
    Math.asin(
      Math.sqrt(
        Math.sin((lat1 - lat2) / 2) ** 2 +
          Math.cos(lat1) * Math.cos(lat2) * Math.sin((lon1 - lon2) / 2) ** 2,
      ),
    );
  if (distance === 0) return [[a.longitude, a.latitude]];
  // As pontas são as coordenadas dadas, sem passar pela trigonometria: a linha
  // tem de tocar o pino, e o arredondamento do arco a deixaria a centímetros.
  const points: [number, number][] = [[a.longitude, a.latitude]];
  for (let step = 1; step < steps; step += 1) {
    const fraction = step / steps;
    const weightA = Math.sin((1 - fraction) * distance) / Math.sin(distance);
    const weightB = Math.sin(fraction * distance) / Math.sin(distance);
    const x = weightA * Math.cos(lat1) * Math.cos(lon1) + weightB * Math.cos(lat2) * Math.cos(lon2);
    const y = weightA * Math.cos(lat1) * Math.sin(lon1) + weightB * Math.cos(lat2) * Math.sin(lon2);
    const z = weightA * Math.sin(lat1) + weightB * Math.sin(lat2);
    points.push([toDeg(Math.atan2(y, x)), toDeg(Math.atan2(z, Math.sqrt(x * x + y * y)))]);
  }
  points.push([b.longitude, b.latitude]);
  return points;
}

/** A linha no formato GeoJSON, tipada localmente: o kit não declara
 *  `@types/geojson` (o namespace global `GeoJSON` não existe aqui) e o
 *  `setData` do Mapbox aceita a forma estrutural. */
type RouteLine = {
  type: 'Feature';
  properties: Record<string, never>;
  geometry: { type: 'LineString'; coordinates: [number, number][] };
};

/** Desembrulha a longitude ao longo da linha: cada ponto fica a menos de 180°
 *  do anterior, mesmo que saia de [-180, 180]. O `atan2` do arco devolve sempre
 *  a longitude embrulhada, então um trecho que cruza o antimeridiano (SCL →
 *  AKL) saltava de -180° para +180° e o Mapbox ligava os dois lados do mundo
 *  com uma reta. O Mapbox aceita `LineString` fora do intervalo e é assim que
 *  ele documenta a travessia dos 180°. */
function unwrapLongitudes(coordinates: [number, number][]): [number, number][] {
  let previous: number | undefined;
  return coordinates.map(([longitude, latitude]) => {
    let unwrapped = longitude;
    if (previous !== undefined) {
      while (unwrapped - previous > 180) unwrapped -= 360;
      while (unwrapped - previous < -180) unwrapped += 360;
    }
    previous = unwrapped;
    return [unwrapped, latitude];
  });
}

function routeLine(points: readonly MapCoordinates[]): RouteLine {
  const coordinates: [number, number][] = [];
  for (let index = 0; index < points.length - 1; index += 1) {
    const from = points[index];
    const to = points[index + 1];
    if (from && to) coordinates.push(...greatCircleArc(from, to));
  }
  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'LineString', coordinates: unwrapLongitudes(coordinates) },
  };
}

/** Fonte e camadas só entram com o estilo carregado — antes disso o Mapbox
 *  lança. `idle` e não `load`: o mapa pode já ter carregado quando o trecho é
 *  escolhido (aí `load` nunca mais dispara), e `idle` volta a cada quadro
 *  assentado. */
function whenStyleReady(map: mapboxgl.Map, fn: () => void): () => void {
  if (map.isStyleLoaded()) {
    fn();
    return () => {};
  }
  map.once('idle', fn);
  return () => {
    map.off('idle', fn);
  };
}

function mountRoute(map: mapboxgl.Map, data: RouteLine): void {
  const source = map.getSource<mapboxgl.GeoJSONSource>(ROUTE_SOURCE);
  if (source) {
    source.setData(data);
    return;
  }
  map.addSource(ROUTE_SOURCE, { type: 'geojson', data });
  map.addLayer({
    id: ROUTE_CASING_LAYER,
    type: 'line',
    source: ROUTE_SOURCE,
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': ROUTE_CASING, 'line-width': 6, 'line-opacity': 0.9 },
  });
  map.addLayer({
    id: ROUTE_SOURCE,
    type: 'line',
    source: ROUTE_SOURCE,
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': ROUTE_INK, 'line-width': 2.5, 'line-dasharray': [2, 1.5] },
  });
}

/** As linhas do trajeto: um contorno branco de 5 px sob todas e a tinta de
 *  2,5 px, contínua (a rua) ou tracejada 6 · 6 (a curva de quando a rota não
 *  veio — o traço do Mapbox é em larguras de linha: 2,4 × 2,5 = 6). */
function mountLines(map: mapboxgl.Map, lines: readonly LensMapLine[]): void {
  const data = {
    type: 'FeatureCollection' as const,
    features: lines.map((line) => ({
      type: 'Feature' as const,
      properties: { dashed: line.dashed === true },
      geometry: {
        type: 'LineString' as const,
        coordinates: line.points.map((point): [number, number] => [
          point.longitude,
          point.latitude,
        ]),
      },
    })),
  };
  const source = map.getSource<mapboxgl.GeoJSONSource>(LINES_SOURCE);
  if (source) {
    source.setData(data);
    return;
  }
  const layout = { 'line-cap': 'round', 'line-join': 'round' } as const;
  map.addSource(LINES_SOURCE, { type: 'geojson', data });
  map.addLayer({
    id: LINES_CASING_LAYER,
    type: 'line',
    source: LINES_SOURCE,
    layout,
    paint: { 'line-color': ROUTE_CASING, 'line-width': 5 },
  });
  map.addLayer({
    id: LINES_SOURCE,
    type: 'line',
    source: LINES_SOURCE,
    filter: ['!', ['get', 'dashed']],
    layout,
    paint: { 'line-color': ROUTE_INK, 'line-width': 2.5 },
  });
  map.addLayer({
    id: LINES_DASHED_LAYER,
    type: 'line',
    source: LINES_SOURCE,
    filter: ['get', 'dashed'],
    layout,
    paint: { 'line-color': ROUTE_INK, 'line-width': 2.5, 'line-dasharray': [2.4, 2.4] },
  });
}

/** Tira as camadas (de cima para baixo) e a fonte. A instância pode já ter
 *  sido removida junto com o contêiner: aí qualquer consulta ao estilo lança,
 *  e não há mais nada a limpar. */
function unmountLayers(map: mapboxgl.Map, source: string, layers: readonly string[]): void {
  try {
    for (const layer of layers) if (map.getLayer(layer)) map.removeLayer(layer);
    if (map.getSource(source)) map.removeSource(source);
  } catch {
    /* mapa já desmontado */
  }
}

const unmountRoute = (map: mapboxgl.Map) => {
  unmountLayers(map, ROUTE_SOURCE, [ROUTE_SOURCE, ROUTE_CASING_LAYER]);
};

/** Pinos: marcadores que nascem com a instância e são refeitos quando a lista
 *  muda — o Mapbox não tem "atualizar marcador", e a lista é pequena. */
function useLensPins({
  canRenderMap,
  mapRef,
  instance,
  pins,
  onPinClick,
}: {
  readonly canRenderMap: boolean;
  readonly mapRef: MapRef;
  readonly instance: MapboxInstance | null;
  readonly pins: readonly LensMapPin[];
  readonly onPinClick: (pinId: string) => void;
}): void {
  // A lista chega nova a cada render do chamador; o que acorda o efeito é o
  // CONTEÚDO — a lista só troca de identidade quando ele muda.
  const shownPins = useKeyedValue(pins);
  useEffect(() => {
    const map = liveMap(mapRef, instance);
    if (!canRenderMap || !map) return undefined;
    const markers = shownPins.map((pin) =>
      new (mapbox().Marker)({ element: pinElement(pin, onPinClick), anchor: 'bottom' })
        .setLngLat([pin.coordinates.longitude, pin.coordinates.latitude])
        .addTo(map),
    );
    return () => {
      for (const marker of markers) marker.remove();
    };
  }, [canRenderMap, instance, mapRef, onPinClick, shownPins]);
}

/** O trecho como camada de linha; some quando não há voo em foco. */
function useLensRoute({
  canRenderMap,
  mapRef,
  instance,
  points,
}: {
  readonly canRenderMap: boolean;
  readonly mapRef: MapRef;
  readonly instance: MapboxInstance | null;
  readonly points: readonly MapCoordinates[];
}): void {
  const routePoints = useKeyedValue(points);
  useEffect(() => {
    const map = liveMap(mapRef, instance);
    // Um ponto só não é trecho: sem linha a desenhar.
    if (!canRenderMap || !map || routePoints.length < 2) return undefined;
    const data = routeLine(routePoints);
    const cancel = whenStyleReady(map, () => {
      mountRoute(map, data);
    });
    return () => {
      cancel();
      unmountRoute(map);
    };
  }, [canRenderMap, instance, mapRef, routePoints]);
}

/** As linhas do trajeto como camadas; somem quando não há nenhuma. */
function useLensLines({
  canRenderMap,
  mapRef,
  instance,
  lines,
}: {
  readonly canRenderMap: boolean;
  readonly mapRef: MapRef;
  readonly instance: MapboxInstance | null;
  readonly lines: readonly LensMapLine[];
}): void {
  const shownLines = useKeyedValue(lines);
  useEffect(() => {
    const map = liveMap(mapRef, instance);
    if (!canRenderMap || !map || shownLines.length === 0) return undefined;
    const lines = shownLines;
    const cancel = whenStyleReady(map, () => {
      mountLines(map, lines);
    });
    return () => {
      cancel();
      unmountLayers(map, LINES_SOURCE, [LINES_DASHED_LAYER, LINES_SOURCE, LINES_CASING_LAYER]);
    };
  }, [canRenderMap, instance, mapRef, shownLines]);
}

/** Os cantos da caixa da câmera. Quando os pontos se espalham por mais de 180°
 *  de longitude o caminho curto passa pelo antimeridiano, não pelo meridiano
 *  zero: os pontos a oeste ganham +360° para a caixa fechar sobre o Pacífico em
 *  vez de abrir no mundo inteiro (SCL a -70° e AKL a +174° viram 290° e 174°).
 *  A lista chega sem ordem (pinos) ou em ordem de voo (trecho); a extensão
 *  total é o que decide, não o vizinho. */
function cameraCorners(points: readonly MapCoordinates[]): [number, number][] {
  const longitudes = points.map((point) => point.longitude);
  const crossesAntimeridian = Math.max(...longitudes) - Math.min(...longitudes) > 180;
  return points.map((point) => [
    crossesAntimeridian && point.longitude < 0 ? point.longitude + 360 : point.longitude,
    point.latitude,
  ]);
}

function frameLens(
  map: mapboxgl.Map,
  focus: readonly MapCoordinates[],
  center: MapCoordinates,
  padding: LensMapPadding,
): void {
  const first = focus[0];
  if (!first || focus.length === 1) {
    const only = first ?? center;
    map.easeTo({ center: [only.longitude, only.latitude], zoom: CITY_ZOOM });
    return;
  }
  const corners = cameraCorners(focus);
  const seed = corners[0];
  if (!seed) return;
  const bounds = corners.reduce(
    (box, corner) => box.extend(corner),
    new (mapbox().LngLatBounds)(seed, seed),
  );
  map.fitBounds(bounds, { padding, maxZoom: FOCUS_MAX_ZOOM });
}

/** A câmera enquadra o foco (`cameraFocus`: trecho e pinos acesos); sem foco,
 *  a cidade. Reenquadra quando a instância é recriada e, pelo `onResize`
 *  devolvido, quando o contêiner assenta depois de um resize. */
function useLensCamera({
  canRenderMap,
  mapRef,
  instance,
  center,
  focus,
  padding,
}: {
  readonly canRenderMap: boolean;
  readonly mapRef: MapRef;
  readonly instance: MapboxInstance | null;
  readonly center: MapCoordinates;
  readonly focus: readonly MapCoordinates[];
  readonly padding: LensMapPadding;
}): () => void {
  const framedFocus = useKeyedValue(focus);
  // A cidade também acorda a câmera: sem foco, trocar a lente de cidade
  // deixaria o mapa na cidade anterior até alguém reenquadrar.
  const framedCenter = useKeyedValue(center);
  const framedPadding = useKeyedValue(padding);
  useEffect(() => {
    const map = liveMap(mapRef, instance);
    if (!canRenderMap || !map) return;
    frameLens(map, framedFocus, framedCenter, framedPadding);
  }, [canRenderMap, framedCenter, framedFocus, framedPadding, instance, mapRef]);
  return useCallback(() => {
    const map = liveMap(mapRef, instance);
    if (canRenderMap && map) frameLens(map, framedFocus, framedCenter, framedPadding);
  }, [canRenderMap, framedCenter, framedFocus, framedPadding, instance, mapRef]);
}

/** Só pinos e pontos com coordenada que o Mapbox aceita chegam ao mapa — um
 *  hotel sem coordenada válida sai da lente em vez de derrubá-la. */
function validPins(pins: readonly LensMapPin[]): LensMapPin[] {
  return pins.filter((pin) => validMapCoordinates(pin.coordinates) !== undefined);
}

function validPoints(points: readonly MapCoordinates[] = []): MapCoordinates[] {
  return points.filter((point) => validMapCoordinates(point) !== undefined);
}

/** O que a câmera enquadra: os pinos acesos (os não apagados) e, quando há
 *  trecho ou linhas, os pontos deles também. O trecho sozinho não basta: o hotel da viagem
 *  em foco é um pino aceso FORA do trecho, e só pelo voo ele ficava na borda ou
 *  sob a folga da caixa. Um ponto só não vira trecho (`useLensRoute` não monta
 *  linha), então aí só os pinos respondem. Coordenada repetida (o aeroporto do
 *  trecho é também um pino) entra uma vez. */
function cameraFocus(
  routePoints: readonly MapCoordinates[],
  linePoints: readonly MapCoordinates[],
  shownPins: readonly LensMapPin[],
): MapCoordinates[] {
  const lit = shownPins.filter((pin) => !pin.muted).map((pin) => pin.coordinates);
  const traced = [...(routePoints.length < 2 ? [] : routePoints), ...linePoints];
  if (traced.length === 0) return lit;
  const seen = new Set<string>();
  return [...traced, ...lit].filter((point) => {
    const key = JSON.stringify([point.longitude, point.latitude]);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function LensMap({
  map,
  pins,
  route,
  lines = [],
  padding = FOCUS_PADDING,
  controls,
  onPinClick,
}: Readonly<LensMapProps>): ReactNode {
  const containerRef = useRef<HTMLDivElement>(null);
  const accessToken = map.accessToken?.trim();
  const [initializationFailed, setInitializationFailed] = useMapInitializationFailure(
    accessToken ?? '',
  );
  const center = validMapCoordinates(map.center);
  const canRenderMap = Boolean(accessToken) && !initializationFailed && center !== undefined;
  const { mapRef, instance } = useMapboxInstance({
    accessToken,
    canRenderMap,
    interactive: true,
    nativeControls: false,
    containerRef,
    initialCoordinates: center,
    initialZoom: CITY_ZOOM,
    setInitializationFailed,
  });
  const handlePinClick = useStableCallback((pinId: string) => {
    onPinClick?.(pinId);
  });
  const shownPins = validPins(pins);
  const routePoints = validPoints(route?.points);
  const shownLines = lines
    .map((line) => ({ ...line, points: validPoints(line.points) }))
    .filter((line) => line.points.length > 1);
  const focus = cameraFocus(
    routePoints,
    shownLines.flatMap((line) => line.points),
    shownPins,
  );
  useLensPins({ canRenderMap, mapRef, instance, pins: shownPins, onPinClick: handlePinClick });
  useLensRoute({ canRenderMap, mapRef, instance, points: routePoints });
  useLensLines({ canRenderMap, mapRef, instance, lines: shownLines });
  const reframe = useLensCamera({
    canRenderMap,
    mapRef,
    instance,
    center: center ?? map.center,
    focus,
    padding,
  });
  useMapboxContainerResize({ canRenderMap, containerRef, mapRef, instance, onSettle: reframe });

  if (!canRenderMap) {
    return (
      <section className="iuk-results-map-fallback iuk-lens-map" aria-label={map.fallbackLabel}>
        <p>{map.fallbackLabel}</p>
      </section>
    );
  }
  return (
    <section className="iuk-results-map iuk-lens-map" aria-label={map.fallbackLabel}>
      <div ref={containerRef} className="iuk-results-map-canvas" />
      <MapViewControls
        controls={controls}
        onZoomIn={() => {
          mapRef.current?.zoomIn();
        }}
        onZoomOut={() => {
          mapRef.current?.zoomOut();
        }}
        onRecenter={() => {
          mapRef.current?.easeTo({ center: [center.longitude, center.latitude], zoom: CITY_ZOOM });
        }}
      />
    </section>
  );
}
